// Lightweight offline support for HealthSpan.
//
// Two pieces:
//   1. A read-through cache for selected GET endpoints — every successful
//      response is stashed in localStorage keyed by the request URL. When
//      the user is offline (navigator.onLine === false OR a network error
//      surfaces) the cached payload is returned instead of throwing.
//   2. A write queue — mutating requests (POST/PATCH/DELETE) that fail with
//      a network error are pushed to a queue in localStorage. A flusher
//      runs on `online` events + on app load and re-issues them in order.
//
// No new dependencies (no Workbox, no Dexie). Survives full page reloads
// because everything is on localStorage.

import { api } from "./api";
import { toast } from "sonner";

const CACHE_PREFIX = "hs-cache:";
const QUEUE_KEY = "hs-write-queue";
// We only cache GETs from these prefixes — pages that the athlete needs to
// open mid-gym (week view, history, library, sets) — to keep storage bounded.
const CACHEABLE_PREFIXES = [
  "/programs",
  "/programs/by-week",
  "/workout-logs",
  "/exercises",
  "/wod-library",
  "/movement-library",
  "/users",
  "/stats/block-prs",
];
// Endpoints whose writes are safe to retry blindly — Murph logs, set logs,
// movement adds. Photo uploads + payment endpoints stay online-only because
// they include FormData / Stripe sessions we can't safely replay.
const QUEUEABLE_PREFIXES = [
  "/workout-logs",
  "/programs/",          // includes /programs/:id/days/:d/blocks etc.
  "/exercises",
];

const isCacheable = (url) => CACHEABLE_PREFIXES.some((p) => url.startsWith(p));
const isQueueable = (url) => QUEUEABLE_PREFIXES.some((p) => url.startsWith(p));

const keyOf = (config) => {
  const params = config.params ? "?" + new URLSearchParams(config.params).toString() : "";
  return CACHE_PREFIX + (config.url || "") + params;
};

function readCache(config) {
  try {
    const raw = localStorage.getItem(keyOf(config));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeCache(config, data) {
  try {
    localStorage.setItem(keyOf(config), JSON.stringify({ data, at: Date.now() }));
  } catch { /* quota / private mode — ignore */ }
}

function loadQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); }
  catch { return []; }
}
function saveQueue(q) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); }
  catch { /* ignore */ }
}

function isNetworkError(err) {
  return !err.response && (err.code === "ERR_NETWORK" || err.message === "Network Error");
}

let _flushing = false;
let _online = typeof navigator !== "undefined" ? navigator.onLine : true;

export function isOnline() { return _online; }
export function queueLength() { return loadQueue().length; }

const listeners = new Set();
export function onStatusChange(cb) { listeners.add(cb); return () => listeners.delete(cb); }
function notify() { listeners.forEach((cb) => { try { cb({ online: _online, queued: queueLength() }); } catch { /* ignore */ } }); }

// ── Axios interceptors ───────────────────────────────────────────────
export function installOfflineLayer() {
  // RESPONSE: cache successful GETs; on network error, serve cache or queue write.
  api.interceptors.response.use(
    (res) => {
      if (res.config?.method === "get" && isCacheable(res.config.url || "")) {
        writeCache(res.config, res.data);
      }
      return res;
    },
    async (err) => {
      const cfg = err?.config || {};
      if (isNetworkError(err)) {
        if ((cfg.method || "").toLowerCase() === "get" && isCacheable(cfg.url || "")) {
          const cached = readCache(cfg);
          if (cached) {
            // Resolve with a synthetic response so callers don't see a failure.
            return { data: cached.data, status: 200, statusText: "OK (offline cache)", config: cfg, headers: {}, fromCache: true };
          }
        }
        const method = (cfg.method || "").toLowerCase();
        if (["post", "patch", "put", "delete"].includes(method) && isQueueable(cfg.url || "")) {
          const queue = loadQueue();
          queue.push({
            id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            url: cfg.url,
            method,
            data: cfg.data,
            params: cfg.params,
            at: Date.now(),
          });
          saveQueue(queue);
          notify();
          toast.info(`Saved offline (${queue.length} in queue) — will sync when reconnected.`);
          // Return a soft success so the UI moves on. The caller gets `null`.
          return { data: null, status: 202, statusText: "Queued offline", config: cfg, headers: {}, queued: true };
        }
      }
      return Promise.reject(err);
    },
  );

  if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
      _online = true; notify();
      flushQueue();
    });
    window.addEventListener("offline", () => { _online = false; notify(); });
    // On boot, attempt to drain any backlog.
    setTimeout(() => { flushQueue(); }, 1500);
  }
}

export async function flushQueue() {
  if (_flushing) return { sent: 0, remaining: queueLength() };
  _flushing = true;
  try {
    let queue = loadQueue();
    const sent = [];
    const failed = [];
    while (queue.length) {
      const job = queue[0];
      try {
        const body = job.data ? (typeof job.data === "string" ? JSON.parse(job.data) : job.data) : undefined;
        await api.request({ url: job.url, method: job.method, data: body, params: job.params, headers: { "X-Replay": job.id } });
        sent.push(job.id);
        queue = queue.slice(1);
        saveQueue(queue);
      } catch (e) {
        if (isNetworkError(e)) break;          // still offline — stop, keep queue
        failed.push({ id: job.id, error: e?.response?.status });
        queue = queue.slice(1);                 // permanent failure — drop and continue
        saveQueue(queue);
      }
    }
    if (sent.length) toast.success(`Synced ${sent.length} offline action${sent.length > 1 ? "s" : ""}`);
    if (failed.length) toast.warning(`${failed.length} offline action${failed.length > 1 ? "s" : ""} failed permanently — open History to verify.`);
    notify();
    return { sent: sent.length, remaining: queue.length };
  } finally { _flushing = false; }
}
